/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Grid, Skeleton } from '@mui/material';
import React from 'react';
import { AlertBanner } from '@opengeoweb/shared';
import type { Bulletin } from '../../types';
import { BulletinType } from '../../types';
import { ContentDialog } from '../ContentDialog';
import { BulletinHistoryList } from './BulletinHistoryList';
import { BulletinTabs } from './BulletinTabs';
import { BulletinViewer } from './BulletinViewer';
import { useSpaceweatherTranslation } from '../../utils/i18n';
import { useBulletinHistory } from '../../utils/spaceweather-api/hooks';

interface BulletinHistoryDialogProps {
  open: boolean;
  toggleStatus: () => void;
}

const BulletinHistoryDialog: React.FC<BulletinHistoryDialogProps> = ({
  open,
  toggleStatus,
}: BulletinHistoryDialogProps) => {
  const [bulletinType, setBulletinType] = React.useState(
    BulletinType.technical,
  );
  const [selectedBulletin, setSelectedBulletin] = React.useState('');
  const { t } = useSpaceweatherTranslation();
  const {
    data: bulletinHistoryList,
    refetch: refetchBulletinHistoryList,
    isFetching,
    error,
  } = useBulletinHistory();

  React.useEffect(() => {
    if (bulletinHistoryList?.length && !selectedBulletin) {
      setSelectedBulletin(bulletinHistoryList[0].bulletin_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulletinHistoryList]);

  React.useEffect(() => {
    if (open) {
      void refetchBulletinHistoryList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedBulletinItem = React.useMemo(() => {
    if (!bulletinHistoryList?.length || !selectedBulletin) {
      return undefined;
    }

    return bulletinHistoryList.find(
      (bulletin: Bulletin) => bulletin.bulletin_id === selectedBulletin,
    );
  }, [bulletinHistoryList, selectedBulletin]);

  return (
    <ContentDialog
      data-testid="history-dialog"
      disableEscapeKeyDown
      fullWidth
      open={open}
      title={t('bulletin-forecast-history-title')}
      toggleStatus={toggleStatus}
    >
      <Grid container>
        <Grid size={2}>
          {!!bulletinHistoryList && (
            <BulletinHistoryList
              bulletinHistoryList={bulletinHistoryList}
              onSelectBulletin={setSelectedBulletin}
              selectedBulletin={selectedBulletin}
            />
          )}
          {!!isFetching && (
            <Skeleton
              data-testid="history-dialog-loading"
              height={558}
              variant="rectangular"
              width="90%"
            />
          )}
          {!!error && (
            <AlertBanner
              dataTestId="history-dialog-error"
              severity="error"
              title={error.message ? error.message : ''}
            />
          )}
        </Grid>
        <Grid size={10}>
          <Grid alignItems="stretch" container direction="column" spacing={0}>
            <BulletinTabs
              onSetBulletinType={setBulletinType}
              selected={bulletinType}
            />
            <Grid>
              <Box
                sx={{
                  padding: '10px 0px 0px 10px',
                }}
              >
                {!!selectedBulletinItem && (
                  <BulletinViewer
                    bulletin={selectedBulletinItem}
                    bulletinType={bulletinType}
                    smallContent={false}
                  />
                )}
              </Box>
              {!!isFetching && (
                <Skeleton height={500} variant="rectangular" width="100%" />
              )}
              {!!error && (
                <AlertBanner
                  severity="error"
                  title={error.message ? error.message : ''}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ContentDialog>
  );
};

export default BulletinHistoryDialog;
